import type * as React from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Inline alert at the top of a card or page (DESIGN_SYSTEM §4), e.g. "Bank change pending HR
 * approval since 12 Oct": tinted fill, tone icon, optional title, text and action (e.g. Retry)
 * under the text. Icon + text, so meaning never depends on colour alone.
 */
type BannerTone = 'info' | 'warning' | 'success' | 'danger';

const TONES: Readonly<Record<BannerTone, { icon: LucideIcon; className: string; label: string }>> =
  {
    info: { icon: Info, className: 'bg-info-soft [&>svg]:text-info', label: 'Info' },
    warning: {
      icon: TriangleAlert,
      className: 'bg-warning-soft [&>svg]:text-warning',
      label: 'Warning',
    },
    success: {
      icon: CircleCheck,
      className: 'bg-success-soft [&>svg]:text-success',
      label: 'Success',
    },
    danger: {
      icon: CircleAlert,
      className: 'bg-danger-soft [&>svg]:text-danger',
      label: 'Error',
    },
  };

function Banner({
  tone = 'info',
  title,
  children,
  action,
  className,
  live = false,
}: {
  tone?: BannerTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** Announce to screen readers (use for banners that appear after a user action). */
  live?: boolean;
}) {
  const { icon: Icon, className: toneClass, label } = TONES[tone];
  return (
    <div
      data-slot="banner"
      data-tone={tone}
      role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
      className={cn(
        'flex items-start gap-3 rounded-md border border-transparent px-4 py-3 text-body text-foreground',
        toneClass,
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{label}: </span>
        {title ? <p className="text-body-strong">{title}</p> : null}
        {children ? (
          <div className={cn(title ? 'text-muted-foreground' : undefined)}>{children}</div>
        ) : null}
        {action ? <div className="mt-2 flex flex-wrap items-center gap-2">{action}</div> : null}
      </div>
    </div>
  );
}

export { Banner, type BannerTone };
